import { toRenderSlot, type EditorContext, type EditorGraphSlot } from '../state/appState.js';
import type { AppState } from '../state/appState.js';
import { subscribeEditorTargetSynced } from '../editor/editorEvents.js';
import type { NodeEditorController } from './nodeEditor.js';
import {
  updateGraphContextBreadcrumb,
  type GraphContextBreadcrumbSegment,
} from './graphContextBreadcrumb.js';
import {
  getSlotStateFor,
  libraryNameFor,
  type EditorLibraryKind,
  type LibraryKind,
  type ReturnToSceneOptions,
} from './editorTargetInfo.js';

type SceneEditorContext = Extract<EditorContext, { mode: 'layer' }>;

interface GraphSlotActionsController {
  attachLibraryToComponent(kind: LibraryKind, componentId: string | null, libraryId: string, layerId?: string): void;
  createLibraryItem(kind: LibraryKind): string | null;
  detachLibraryToInline(kind: LibraryKind, componentId: string | null, layerId?: string): void;
  libraryName(kind: LibraryKind, id: string): string;
  saveComponentSlotToLibrary(kind: LibraryKind, componentId: string | null, layerId?: string): void;
}

interface EditorNavigationDeps {
  appState: AppState;
  syncEditor: () => boolean;
  loadComponentSlot: (componentId: string | null, slot: LibraryKind, layerId?: string) => boolean;
  loadLibraryItem: (kind: EditorLibraryKind, id: string) => boolean;
  nodeEditor: Pick<NodeEditorController, 'renderGraph'>;
  renderSceneTree: () => void;
  openSceneTreePanel: () => void;
  openNodeEditor: () => void;
  scheduleRender: () => void;
  getGraphSlotActions?: () => GraphSlotActionsController | null;
  syncViewportToolbar?: () => void;
  updateParams: () => void;
}

export interface EditorNavigationController {
  openComponentSlotEditor(componentId: string | null, slot: EditorGraphSlot, syncBefore?: boolean, layerId?: string): void;
  openLibraryEditor(kind: EditorLibraryKind, id: string): void;
  returnToRootScene(): void;
  returnToScene(options?: ReturnToSceneOptions): void;
  render(): void;
}

function isSceneEditorContext(context: EditorContext): context is SceneEditorContext {
  return context.mode === 'layer';
}

function isConcreteSceneEditorContext(context: EditorContext): context is SceneEditorContext {
  return context.mode === 'layer' && !!context.layerId;
}

function isLibraryEditorContext(context: EditorContext): boolean {
  return context.mode === 'geometry-library'
    || context.mode === 'material-library'
    || context.mode === 'data-library'
    || context.mode === 'event-library';
}

function cloneSceneEditorContext(context: SceneEditorContext): SceneEditorContext {
  return { ...context };
}

function slotLabel(slot: EditorGraphSlot | LibraryKind): string {
  if (slot === 'material') return 'Material';
  return 'Geometry';
}

function visibleLibraryItems(appState: AppState, kind: LibraryKind): Array<[string, { name: string }]> {
  const groups = [
    kind === 'geometry' ? appState.geometryLibrary.builtIn : appState.materialLibrary.builtIn,
    kind === 'geometry' ? appState.geometryLibrary.user : appState.materialLibrary.user,
  ];

  return groups.flatMap(group => Object.entries(group)
    .map(([id, item]) => [id, item] as [string, { name: string }]));
}

function refreshEditorTargetUI(
  deps: EditorNavigationDeps,
  renderEditorContextInfo: () => void,
  renderScene = true,
): void {
  deps.nodeEditor.renderGraph();
  deps.updateParams();
  if (renderScene) deps.renderSceneTree();
  renderEditorContextInfo();
  deps.syncViewportToolbar?.();
  deps.scheduleRender();
}

export function createEditorNavigationController(deps: EditorNavigationDeps): EditorNavigationController {
  const { appState } = deps;
  let lastSceneEditorContext: SceneEditorContext | null = isConcreteSceneEditorContext(appState.editorContext)
    ? cloneSceneEditorContext(appState.editorContext)
    : null;

  function rememberSceneEditorContext(context: EditorContext = appState.editorContext): void {
    if (!isConcreteSceneEditorContext(context)) return;
    lastSceneEditorContext = cloneSceneEditorContext(context);
  }

  function loadSceneEditorContext(context: SceneEditorContext): boolean {
    return deps.loadComponentSlot(
      context.componentId ?? null,
      context.layerSlot ?? 'geometry',
      context.layerId,
    );
  }

  function clearSceneEditorTarget(componentId: string | null = appState.activeComponentId): void {
    const validComponentId = componentId && appState.componentLibrary[componentId] ? componentId : null;
    appState.activeComponentId = validComponentId;
    appState.editorContext = {
      mode: 'layer',
      componentId: validComponentId ?? undefined,
      layerId: undefined,
      layerSlot: toRenderSlot(appState.editorGraphSlot),
    };
    appState.nodes = [];
    appState.edges = [];
    appState.editorGraphDirty = false;
    appState.selectedNode = null;
  }

  function returnToSceneEditorTarget(options: ReturnToSceneOptions = {}): void {
    if (isLibraryEditorContext(appState.editorContext)) {
      deps.syncEditor();
      const rememberedContext = lastSceneEditorContext ? cloneSceneEditorContext(lastSceneEditorContext) : null;
      if (!rememberedContext || !loadSceneEditorContext(rememberedContext)) {
        clearSceneEditorTarget(rememberedContext?.componentId ?? appState.activeComponentId);
      }
    }
    refreshEditorTargetUI(deps, renderEditorContextInfo);
    if (options.openScenePanel) deps.openSceneTreePanel();
  }

  function returnToRootScene(): void {
    deps.syncEditor();
    clearSceneEditorTarget(null);
    refreshEditorTargetUI(deps, renderEditorContextInfo);
  }

  function openLibraryEditor(kind: EditorLibraryKind, id: string): void {
    rememberSceneEditorContext();
    deps.syncEditor();
    if (!deps.loadLibraryItem(kind, id)) return;
    refreshEditorTargetUI(deps, renderEditorContextInfo, false);
    deps.openNodeEditor();
  }

  function renderEditorContextInfo(): void {
    const host = document.getElementById('editor-context-info');
    if (host) host.innerHTML = '';
    const slotActionsHost = document.getElementById('graph-slot-actions') as HTMLElement | null;
    renderGraphSlotActions(slotActionsHost);

    const ctx = appState.editorContext;
    if (isSceneEditorContext(ctx)) rememberSceneEditorContext(ctx);

    const primary = document.createElement('span');
    primary.className = 'editor-context-primary';
    const secondary = document.createElement('span');
    secondary.className = 'editor-context-secondary';
    const segments: GraphContextBreadcrumbSegment[] = [];
    let backLabel: string | undefined;
    let backTitle: string | undefined;
    let onBack: (() => void) | undefined;

    if (ctx.mode === 'layer' && ctx.layerId) {
      const component = ctx.componentId ? appState.componentLibrary[ctx.componentId] : null;
      const layerDef = appState.layerLibrary[ctx.layerId];
      const slotKind = ctx.layerSlot ?? 'geometry';
      const slot = slotKind === 'geometry' ? layerDef?.geometry : layerDef?.material;
      if (!layerDef || !slot || (ctx.componentId && !component)) {
        primary.textContent = `Layer / ${slotKind}`;
        secondary.textContent = 'Missing layer';
        segments.push(
          { label: ctx.componentId ? 'Component' : 'Scene', muted: true },
          { label: 'Missing layer', current: true },
          { label: slotLabel(slotKind), muted: true },
        );
      } else {
        const slotState = getSlotStateFor(appState, slotKind, slot);
        const ownerName = component?.name ?? 'Scene';
        primary.textContent = `${ownerName} / ${layerDef.name} / ${slotKind}`;
        secondary.textContent = `${slotState.status}: ${slotState.detail}`;
        if (component) {
          segments.push(
            { label: 'Scene', title: 'Back to root scene', onClick: returnToRootScene },
            { label: `Component: ${component.name}`, title: component.name },
          );
        } else {
          segments.push({ label: 'Scene' });
        }
        segments.push(
          { label: `Layer: ${layerDef.name}`, title: layerDef.name },
          { label: slotLabel(slotKind), current: true },
        );
      }
    } else if (ctx.mode === 'geometry-library' && ctx.libraryItemId) {
      primary.textContent = `Library / Geometry / ${libraryNameFor(appState, 'geometry', ctx.libraryItemId)}`;
      const item = appState.geometryLibrary.builtIn[ctx.libraryItemId] ?? appState.geometryLibrary.user[ctx.libraryItemId];
      secondary.textContent = item?.builtIn ? 'Built-in library item; editing creates a user copy' : 'Direct edit of user library item';
      backLabel = 'Back to scene';
      backTitle = 'Return to the previous scene graph target';
      onBack = () => returnToSceneEditorTarget({ openScenePanel: true });
      segments.push(
        { label: 'Library', muted: true },
        { label: 'Geometry' },
        { label: libraryNameFor(appState, 'geometry', ctx.libraryItemId), title: ctx.libraryItemId, current: true },
      );
    } else if (ctx.mode === 'material-library' && ctx.libraryItemId) {
      primary.textContent = `Library / Material / ${libraryNameFor(appState, 'material', ctx.libraryItemId)}`;
      const item = appState.materialLibrary.builtIn[ctx.libraryItemId] ?? appState.materialLibrary.user[ctx.libraryItemId];
      secondary.textContent = item?.builtIn ? 'Built-in library item; editing creates a user copy' : 'Direct edit of user library item';
      backLabel = 'Back to scene';
      backTitle = 'Return to the previous scene graph target';
      onBack = () => returnToSceneEditorTarget({ openScenePanel: true });
      segments.push(
        { label: 'Library', muted: true },
        { label: 'Material' },
        { label: libraryNameFor(appState, 'material', ctx.libraryItemId), title: ctx.libraryItemId, current: true },
      );
    } else if (ctx.mode === 'data-library' && ctx.libraryItemId) {
      primary.textContent = `Data Graph / ${ctx.libraryItemId}`;
      secondary.textContent = 'Runtime behavior graph';
      segments.push(
        { label: 'Data Graphs', muted: true },
        { label: ctx.libraryItemId, current: true },
      );
    } else if (ctx.mode === 'event-library' && ctx.libraryItemId) {
      primary.textContent = `Event Graph / ${ctx.libraryItemId}`;
      secondary.textContent = 'Typed runtime state commands';
      segments.push(
        { label: 'Event Graphs', muted: true },
        { label: ctx.libraryItemId, current: true },
      );
    } else if (ctx.mode === 'layer' && !ctx.layerId) {
      const component = ctx.componentId ? appState.componentLibrary[ctx.componentId] : null;
      primary.textContent = component ? `${component.name} / No editor target` : 'Scene / No editor target';
      secondary.textContent = 'Select a layer or slot';
      if (component) {
        segments.push(
          { label: 'Scene', title: 'Back to root scene', onClick: returnToRootScene },
          { label: `Component: ${component.name}`, title: component.name },
        );
      } else {
        segments.push({ label: 'Scene' });
      }
      segments.push({ label: 'No editor target', muted: true, current: true });
    } else {
      primary.textContent = 'No editor target';
      secondary.textContent = 'Select a component or slot';
      segments.push({ label: 'No editor target', muted: true, current: true });
    }

    if (host) {
      host.appendChild(primary);
      host.appendChild(secondary);
    }
    updateGraphContextBreadcrumb({
      backLabel,
      backTitle,
      onBack,
      segments,
    });
  }

  function renderGraphSlotActions(host: HTMLElement | null): void {
    if (!host) return;
    host.innerHTML = '';
    host.hidden = true;
    const actions = deps.getGraphSlotActions?.() ?? null;
    const ctx = appState.editorContext;
    if (!actions || ctx.mode !== 'layer' || !ctx.layerId) return;

    const layerDef = appState.layerLibrary[ctx.layerId];
    if (!layerDef) return;
    const kind = ctx.layerSlot ?? 'geometry';
    const componentId = ctx.componentId ?? null;
    const slot = kind === 'geometry' ? layerDef.geometry : layerDef.material;

    host.hidden = false;

    const sourceSelect = document.createElement('select');
    sourceSelect.className = 'graph-slot-source-select';
    sourceSelect.title = `${slotLabel(kind)} source`;

    if (slot.type === 'inline') {
      const inlineOption = document.createElement('option');
      inlineOption.value = '__inline__';
      inlineOption.textContent = 'Inline';
      inlineOption.selected = true;
      sourceSelect.appendChild(inlineOption);
    } else {
      const currentOption = document.createElement('option');
      currentOption.value = slot.id;
      currentOption.textContent = actions.libraryName(kind, slot.id);
      currentOption.selected = true;
      sourceSelect.appendChild(currentOption);
    }

    if (slot.type !== 'inline') {
      const inlineOption = document.createElement('option');
      inlineOption.value = '__inline__';
      inlineOption.textContent = 'Inline';
      sourceSelect.appendChild(inlineOption);
    }

    for (const [id, item] of visibleLibraryItems(appState, kind)) {
      if (slot.type === 'library' && id === slot.id) continue;
      const opt = document.createElement('option');
      opt.value = id;
      opt.textContent = item.name;
      sourceSelect.appendChild(opt);
    }

    const commandSeparator = document.createElement('option');
    commandSeparator.disabled = true;
    commandSeparator.textContent = '────────';
    sourceSelect.appendChild(commandSeparator);

    if (slot.type === 'library') {
      const makeInlineOption = document.createElement('option');
      makeInlineOption.value = '__make_inline__';
      makeInlineOption.textContent = 'Make inline';
      sourceSelect.appendChild(makeInlineOption);

      const openLibraryOption = document.createElement('option');
      openLibraryOption.value = '__open_library__';
      openLibraryOption.textContent = 'Open library';
      sourceSelect.appendChild(openLibraryOption);
    } else {
      const saveOption = document.createElement('option');
      saveOption.value = '__save_library__';
      saveOption.textContent = 'Save to library';
      sourceSelect.appendChild(saveOption);
    }

    const newLibraryOption = document.createElement('option');
    newLibraryOption.value = '__new_library__';
    newLibraryOption.textContent = 'New library item';
    sourceSelect.appendChild(newLibraryOption);

    sourceSelect.addEventListener('change', () => {
      if (sourceSelect.value === '__save_library__') {
        actions.saveComponentSlotToLibrary(kind, componentId, ctx.layerId);
        renderEditorContextInfo();
        return;
      }
      if (sourceSelect.value === '__new_library__') {
        const id = actions.createLibraryItem(kind);
        if (id) actions.attachLibraryToComponent(kind, componentId, id, ctx.layerId);
        renderEditorContextInfo();
        return;
      }
      if (sourceSelect.value === '__make_inline__') {
        actions.detachLibraryToInline(kind, componentId, ctx.layerId);
        return;
      }
      if (sourceSelect.value === '__open_library__') {
        if (slot.type === 'library') openLibraryEditor(kind, slot.id);
        return;
      }
      if (sourceSelect.value === '__inline__') {
        if (slot.type === 'library') actions.detachLibraryToInline(kind, componentId, ctx.layerId);
        return;
      }
      actions.attachLibraryToComponent(kind, componentId, sourceSelect.value, ctx.layerId);
    });
    host.appendChild(sourceSelect);
  }

  const controller: EditorNavigationController = {
    openComponentSlotEditor(componentId, slot, syncBefore = true, layerId): void {
      if (syncBefore) deps.syncEditor();
      if (!deps.loadComponentSlot(componentId, toRenderSlot(slot), layerId)) return;
      refreshEditorTargetUI(deps, renderEditorContextInfo);
      deps.openNodeEditor();
    },
    openLibraryEditor,
    returnToRootScene,
    returnToScene: returnToSceneEditorTarget,
    render: renderEditorContextInfo,
  };

  return controller;
}

export function initEditorNavigation(deps: EditorNavigationDeps): EditorNavigationController {
  const controller = createEditorNavigationController(deps);
  subscribeEditorTargetSynced(controller.render);
  controller.render();
  return controller;
}
